"use client";

import { useCallback, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { useAuthStore } from "@/stores/authStore";
import { ChevronDown, Loader2 } from "lucide-react";
import { Card, CardHeader, CardContent } from "@/components/ui/card";
import { ASSETS } from "@/config";
import { useData } from "@/stores/dataStore";
import { Loader } from "@/components/common";

interface LanguageSelectorProps {
  selectedLang: string;
  disabled: boolean;
  onChangeLanguage: (lang: string) => void;
}

const LanguageSelector = ({
  selectedLang,
  disabled,
  onChangeLanguage,
}: LanguageSelectorProps) => {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          className="flex items-center font-glacial rounded-full"
          variant="outline"
          size="sm"
          disabled={disabled}
        >
          {selectedLang === "si" ? "සිංහල" : "English"}
          <ChevronDown className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="font-glacial bg-primary-foreground/5 backdrop-blur-sm border border-primary-foreground/10 rounded-2xl hover:bg-primary-foreground/10 transition-all duration-300">
        <DropdownMenuItem
          className="text-primary"
          onClick={() => onChangeLanguage("en")}
        >
          English
        </DropdownMenuItem>
        <DropdownMenuItem
          className="text-primary"
          onClick={() => onChangeLanguage("si")}
        >
          සිංහල
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

const LandingPageV2 = () => {
  const { t, i18n } = useTranslation();
  const [selectedLang, setSelectedLang] = useState("en");
  const [mounted, setMounted] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuthStore();
  const { isLoading: isLoadingPublicData } = useData();

  // Mutation for login
  const loginMutation = useMutation({
    mutationFn: async () => {
      login();
    },
    onError: (error) => {
      console.error("Login failed:", error);
    },
  });

  const onChangeLanguage = (lng: string) => {
    i18n.changeLanguage(lng);
    setSelectedLang(lng);
  };

  const handleLogin = useCallback(() => {
    if (!loginMutation.isPending) {
      loginMutation.mutate();
    }
  }, [loginMutation]);

  const handleSignup = useCallback(() => {
    if (!location.pathname.startsWith("/signup")) {
      navigate("/signup");
    }
  }, [navigate, location]);

  const renderTranslatedText = (key: string) => {
    if (!mounted) return key; // Return the key during SSR
    const translated = t(key);
    return typeof translated === "string" ? translated : key;
  };

  const isLoading = loginMutation.isPending;

  const features = [
    {
      icon: ASSETS.LANDING_PAGE_JOIN,
      title: "Join Us",
      points: [
        {
          title: "Sign Up",
          description:
            "Become a member by visiting any of our retail outlets or signing up online through our website.",
        },
        {
          title: "Registration",
          description:
            "Simply provide your name, contact information, and create an account. It's quick and easy!",
        },
        {
          title: "Welcome Bonus",
          description:
            "As a new member, receive 250 bonus points just for signing up.",
        },
      ],
    },
    {
      icon: ASSETS.LANDING_PAGE_EARN,
      title: "Earn Points",
      points: [
        {
          title: "How It Works",
          description:
            "Earn 1 loyalty point for every Rs 200 spent in-store or online.",
        },
        {
          title: "Bonus Points",
          description:
            "Enjoy special promotions where you can earn extra points on specific products or during special events.",
        },
        {
          title: "Track Your Points",
          description:
            "Easily track your points and rewards through our mobile app or website.",
        },
      ],
    },
    {
      icon: ASSETS.LANDING_PAGE_REDEEM,
      title: "Redeem Points",
      points: [
        {
          title: "Use Your Points",
          description:
            "Minimum number of points required to be able to redeem is 500 points, redeem them for exciting rewards such as discounts, free products, or exclusive experiences.",
        },
        {
          title: "Redemption Process",
          description:
            "Simply select your reward during checkout, either in-store or online, and apply your points toward the total purchase amount.",
        },
        {
          title: "Points Expiry",
          description:
            "Don't forget to use your points before they expire! Points are valid for 12 months from the date they're earned.",
        },
      ],
    },
  ];

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    onChangeLanguage("en");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (isLoadingPublicData)
    return (
      <div className="min-h-screen bg-background font-perpetua">
        <Loader />
      </div>
    );

  return (
    <div className="min-h-screen bg-background font-perpetua">
      {/* Header Section */}
      <div className="mx-auto sticky top-0 z-50 bg-background">
        <div className="flex flex-col md:flex-row justify-between items-center p-6">
          <div className="md:hidden md:block min-w-[100px] w-full flex-1 flex justify-end">
            <LanguageSelector
              selectedLang={selectedLang}
              disabled={isLoading}
              onChangeLanguage={onChangeLanguage}
            />
          </div>
          <div className="w-full flex-1 flex justify-center md:justify-start">
            <div className="py-6 md:py-3 relative w-[10rem]">
              <img
                className="min-landing-logo space-y-8 hover:scale-105 transition-transform duration-300 object-contain"
                src={ASSETS.LANDING_PAGE_LOGO}
                alt="Celeste"
                sizes="(max-width: 768px) 100vw, (max-width: 1200px) 100vw, 33vw"
                style={{
                  objectFit: "contain",
                }}
              />
            </div>
          </div>
          <div className="flex justify-center md:justify-end gap-4 items-center w-full">
            <Button
              className="text-lg px-8 font-glacial sm:w-auto"
              variant="outline"
              size="lg"
              disabled={isLoading}
              onClick={handleLogin}
            >
              {isLoading ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : null}
              {renderTranslatedText("Login")}
            </Button>
            <Button
              className="text-lg px-8 font-glacial sm:w-auto"
              variant="secondary"
              size="lg"
              disabled={isLoading}
              onClick={handleSignup}
            >
              {renderTranslatedText("Sign Up")}
            </Button>
            <div className="hidden md:block">
              <LanguageSelector
                selectedLang={selectedLang}
                disabled={isLoading}
                onChangeLanguage={onChangeLanguage}
              />
            </div>
          </div>
        </div>

        {/* Gold Horizontal Line */}
        <div className="relative">
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-yellow-400 to-transparent opacity-20 blur-sm" />
          <div className="w-full h-[2px] bg-gradient-to-r from-transparent via-yellow-500 to-transparent opacity-80" />
        </div>
      </div>

      {/* Hero Section */}
      <div className="container mx-auto px-4 py-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div className="text-primary space-y-8 text-center sm:text-left">
            <h1 className="text-5xl text-primary font-bold ">
              {renderTranslatedText("Your Loyalty")}
              <br />
              {renderTranslatedText("Our Gratitude!")}
            </h1>
            <p className="text-lg text-primary/80 max-w-md mx-auto sm:mx-0 font-glacial">
              {renderTranslatedText("Join our exclusive loyalty program and start earning rewards today.")}
            </p>
            <div className="flex justify-center sm:justify-start">
              <Button
                className="rounded-full text-lg px-8 font-glacial w-[200px] sm:w-auto"
                variant="secondary"
                size="lg"
                onClick={handleSignup}
                disabled={isLoading}
              >
                {renderTranslatedText("Join Now")}
              </Button>
            </div>
          </div>
          <div className="flex justify-center">
            <div className="relative">
              <img
                className="landing-logo hover:scale-105 transition-transform duration-300 object-contain"
                src={ASSETS.LANDING_PAGE_LOGO}
                alt="Celeste"
                sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                style={{
                  objectFit: "contain",
                }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Gold Horizontal Line */}
      <div className="relative mb-20">
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-yellow-400 to-transparent opacity-20 blur-sm" />
        <div className="w-full h-[2px] bg-gradient-to-r from-transparent via-yellow-500 to-transparent opacity-80" />
      </div>

      {/* Features Section */}
      <div className="container mx-auto px-4 py-20">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-primary mb-4 font-perpetua">
            {renderTranslatedText("Getting Started is Easy!")}
          </h2>
          <p className="text-xl text-primary/70 font-glacial">
            {renderTranslatedText("Join our family and start earning points")}
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {features.map((feature, index) => (
            <Card
              key={index}
              className="group bg-primary-foreground/5 backdrop-blur-sm border border-primary-foreground/10 rounded-2xl p-8 hover:bg-primary-foreground/10 transition-all duration-300"
            >
              <CardHeader>
                <div className="flex flex-col items-center text-center space-y-6">
                  <div className="p-4 rounded-full bg-primary-foreground/10 group-hover:bg-primary-foreground/20 transition-colors border border-yellow-500/30">
                    <img
                      src={feature.icon}
                      alt={feature.title}
                      width={60}
                      height={60}
                      className="brightness-0 invert-0"
                    />
                  </div>
                  <h3 className="text-2xl font-semibold text-primary font-perpetua">
                    {renderTranslatedText(feature.title)}
                  </h3>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-4 text-left">
                  {feature.points.map((point, idx) => (
                    <div key={idx} className="flex flex-col space-y-1">
                      <h4 className="text-primary font-semibold font-perpetua text-lg">
                        {renderTranslatedText(point.title)}
                      </h4>
                      <p className="text-primary leading-relaxed font-glacial text-sm">
                        {renderTranslatedText(point.description)}
                      </p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
      <footer className="border-t border-primary-foreground/10 mt-20">
        <div className="container mx-auto px-4 py-8">
          <div className="text-center text-primary/60 font-glacial">
            <p>
              {`Powered by `}
              <a
                href="https://www.cxforge.com/"
                rel="noopener noreferrer"
                target="_blank"
                className="hover:text-primary transition-colors"
              >
                ShoutOUT (CX Forge)
              </a>
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPageV2;
